import { Injectable, inject, signal } from '@angular/core';
import { ApiClientService } from '../core/api/api-client.service';
import { AppStore } from '../stores/app.store';
import { AuthStore, AuthUser } from '../stores/auth.store';
import { ApiResponse } from '../shared/models/types';
import { JwtHelperService } from '@auth0/angular-jwt';
import { LogUtilityService } from '../core/logging/log-utility.service';

interface SendVerificationRequest {
  phoneNumber: string;
}

interface SendVerificationResponse extends ApiResponse {
  verificationStatus: string;
  isVerified: boolean;
}

interface LoginRequest {
  phoneNumber: string;
  code: string;
}

interface LoginResponse extends ApiResponse {
  token: string;
  user: AuthUser;
}

interface RefreshTokenResponse extends ApiResponse {
  token: string;
  user: AuthUser;
}

interface SignupRequest {
  firstName: string;
  lastName: string;
  email: string;
  organizationName: string;
  phoneNumber: string;
  code: string;
}

interface SignupResponse extends LoginResponse {
  organizationId: Guid;
}

@Injectable({ providedIn: 'root' })
export class AuthManagerService {
  private readonly apiClient = inject(ApiClientService);
  private readonly authStore = inject(AuthStore);
  private readonly appStore = inject(AppStore);
  private readonly logUtility = inject(LogUtilityService);
  private readonly jwtHelper = new JwtHelperService();

  readonly isTokenReady = signal(false);

  async initialize(): Promise<boolean> {
    const token = this.authStore.loadTokenFromStorage();
    if (!token) {
      return false;
    }

    if (!this.jwtHelper.isTokenExpired(token)) {
      this.isTokenReady.set(true);
      return true;
    }

    // Token exists but expired — attempt refresh
    return this.refreshAuth();
  }

  async refreshAuth(): Promise<boolean> {
    const maxRetries = 3;
    const retryDelay = 300;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const result = await this.apiClient.get<RefreshTokenResponse>('auth/refresh');
        if (result.success && result.token) {
          this.authStore.setAuth(result.token, result.user);
          this.applyUserContextToLogging();
          this.isTokenReady.set(true);
          return true;
        }
        break;
      } catch {
        if (attempt < maxRetries) {
          await new Promise((r) => setTimeout(r, retryDelay));
        }
      }
    }

    this.authStore.clearAuth();
    this.isTokenReady.set(false);
    return false;
  }

  async sendVerificationCode(phoneNumber: string): Promise<SendVerificationResponse> {
    return this.apiClient.post<SendVerificationRequest, SendVerificationResponse>(
      'auth/send-login-verification',
      { phoneNumber }
    );
  }

  async login(phoneNumber: string, code: string): Promise<LoginResponse> {
    const result = await this.apiClient.post<LoginRequest, LoginResponse>(
      'auth/login',
      { phoneNumber, code }
    );
    if (result.success) {
      this.authStore.setAuth(result.token, result.user);
      this.applyUserContextToLogging();
      this.isTokenReady.set(true);
    }
    return result;
  }

  async sendSignupCode(phoneNumber: string): Promise<SendVerificationResponse> {
    return this.apiClient.post<SendVerificationRequest, SendVerificationResponse>(
      'auth/send-admin-sign-up-verification',
      { phoneNumber }
    );
  }

  async signUp(signup: SignupRequest): Promise<SignupResponse> {
    const result = await this.apiClient.post<SignupRequest, SignupResponse>(
      'auth/admin-sign-up',
      signup
    );
    if (result.success) {
      this.authStore.setAuth(result.token, result.user);
      this.appStore.setSelectedOrganizationId(result.organizationId);
      this.applyUserContextToLogging();
      this.isTokenReady.set(true);
    }
    return result;
  }

  logout() {
    this.authStore.clearAuth();
    this.logUtility.clearAuthenticatedUser();
    this.isTokenReady.set(false);
  }

  isAuthenticated(): boolean {
    const token = this.authStore.token();
    return !!token && !this.jwtHelper.isTokenExpired(token);
  }

  private applyUserContextToLogging(): void {
    const user = this.authStore.user();
    if (user) {
      this.logUtility.setAuthenticatedUser(user.id, {
        email: user.email,
        name: `${user.firstName} ${user.lastName}`,
        roles: this.authStore.roles().join(','),
      });
    }
  }
}
